/**
 * Form validation utility for SoloSocial
 * Handles edge cases including extremely long inputs, special characters, and comprehensive error messages
 */

import { logError, logWarning } from './logger';

// Validation constants
export const VALIDATION_LIMITS = {
  NAME_MIN_LENGTH: 1,
  NAME_MAX_LENGTH: 50,
  EMAIL_MAX_LENGTH: 254, // RFC 5321 standard
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,
  CONNECTION_MAX_LENGTH: 100,
};

// Regular expressions for validation
const REGEX_PATTERNS = {
  EMAIL: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
  NAME_ALLOWED_CHARS: /^[a-zA-Z\s'-]+$/,
  STRONG_PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
  WEAK_PASSWORD: /^(?=.*[a-zA-Z])(?=.*\d)/,
};

/**
 * Validates a required field
 * @param {string} value - Field value
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} Error message or null if valid
 */
export const validateRequired = (value, fieldName) => {
  if (!value || typeof value !== 'string' || value.trim().length === 0) {
    return `${fieldName} is required`;
  }
  return null;
};

/**
 * Validates first name
 * @param {string} firstName - First name value
 * @returns {string|null} Error message or null if valid
 */
export const validateFirstName = (firstName) => {
  // Check if required
  const requiredError = validateRequired(firstName, 'First name');
  if (requiredError) return requiredError;

  const trimmedName = firstName.trim();

  // Check length limits
  if (trimmedName.length < VALIDATION_LIMITS.NAME_MIN_LENGTH) {
    return 'First name must be at least 1 character long';
  }
  
  if (trimmedName.length > VALIDATION_LIMITS.NAME_MAX_LENGTH) {
    logWarning('Extremely long first name attempted', { length: trimmedName.length });
    return `First name cannot exceed ${VALIDATION_LIMITS.NAME_MAX_LENGTH} characters`;
  }

  // Check for valid characters (letters, spaces, hyphens, apostrophes)
  if (!REGEX_PATTERNS.NAME_ALLOWED_CHARS.test(trimmedName)) {
    logWarning('Invalid characters in first name', { firstName: trimmedName });
    return 'First name can only contain letters, spaces, hyphens, and apostrophes';
  }

  // Check for suspicious patterns (all numbers, excessive special chars)
  if (/^\d+$/.test(trimmedName)) {
    return 'First name cannot be only numbers';
  }

  // Check for excessive repeating characters
  if (/(.)\1{4,}/.test(trimmedName)) {
    return 'First name contains too many repeating characters';
  }

  return null;
};

/**
 * Validates last name
 * @param {string} lastName - Last name value
 * @returns {string|null} Error message or null if valid
 */
export const validateLastName = (lastName) => {
  // Check if required
  const requiredError = validateRequired(lastName, 'Last name');
  if (requiredError) return requiredError;

  const trimmedName = lastName.trim();

  // Check length limits
  if (trimmedName.length < VALIDATION_LIMITS.NAME_MIN_LENGTH) {
    return 'Last name must be at least 1 character long';
  }
  
  if (trimmedName.length > VALIDATION_LIMITS.NAME_MAX_LENGTH) {
    logWarning('Extremely long last name attempted', { length: trimmedName.length });
    return `Last name cannot exceed ${VALIDATION_LIMITS.NAME_MAX_LENGTH} characters`;
  }

  // Check for valid characters
  if (!REGEX_PATTERNS.NAME_ALLOWED_CHARS.test(trimmedName)) {
    logWarning('Invalid characters in last name', { lastName: trimmedName });
    return 'Last name can only contain letters, spaces, hyphens, and apostrophes';
  }

  // Check for suspicious patterns
  if (/^\d+$/.test(trimmedName)) {
    return 'Last name cannot be only numbers';
  }

  // Check for excessive repeating characters
  if (/(.)\1{4,}/.test(trimmedName)) {
    return 'Last name contains too many repeating characters';
  }

  return null;
};

/**
 * Validates email address
 * @param {string} email - Email value
 * @returns {string|null} Error message or null if valid
 */
export const validateEmail = (email) => {
  // Check if required
  const requiredError = validateRequired(email, 'Email');
  if (requiredError) return requiredError;

  const trimmedEmail = email.trim().toLowerCase();

  // Check length limits
  if (trimmedEmail.length > VALIDATION_LIMITS.EMAIL_MAX_LENGTH) {
    logWarning('Extremely long email attempted', { length: trimmedEmail.length });
    return `Email cannot exceed ${VALIDATION_LIMITS.EMAIL_MAX_LENGTH} characters`;
  }

  // Check email format
  if (!REGEX_PATTERNS.EMAIL.test(trimmedEmail)) {
    return 'Please enter a valid email address';
  }

  // Additional email security checks
  const emailParts = trimmedEmail.split('@');
  if (emailParts.length !== 2) {
    return 'Please enter a valid email address';
  }

  const [localPart, domain] = emailParts;

  // Check local part length (before @)
  if (localPart.length > 64) {
    return 'Email address is too long';
  }

  // Check for suspicious patterns
  if (localPart.includes('..') || localPart.startsWith('.') || localPart.endsWith('.')) {
    return 'Email format is invalid';
  }

  // Check domain part
  if (domain.length > 253) {
    return 'Email domain is too long';
  }

  // Check for valid domain format
  if (!/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(domain)) {
    return 'Email domain format is invalid';
  }

  return null;
};

/**
 * Validates password
 * @param {string} password - Password value
 * @param {boolean} isStrict - Whether to enforce strict password requirements
 * @returns {string|null} Error message or null if valid
 */
export const validatePassword = (password, isStrict = false) => {
  // Check if required
  const requiredError = validateRequired(password, 'Password');
  if (requiredError) return requiredError;

  // Check length limits
  if (password.length < VALIDATION_LIMITS.PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${VALIDATION_LIMITS.PASSWORD_MIN_LENGTH} characters long`;
  }

  if (password.length > VALIDATION_LIMITS.PASSWORD_MAX_LENGTH) {
    logWarning('Extremely long password attempted', { length: password.length });
    return `Password cannot exceed ${VALIDATION_LIMITS.PASSWORD_MAX_LENGTH} characters`;
  }

  // Strict password validation (for production)
  if (isStrict) {
    if (!REGEX_PATTERNS.STRONG_PASSWORD.test(password)) {
      return 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }
  } else {
    // Relaxed validation (for development/testing)
    if (!REGEX_PATTERNS.WEAK_PASSWORD.test(password)) {
      return 'Password must contain at least one letter and one number';
    }
  }

  // Check for common weak patterns
  const commonWeakPasswords = [
    'password', '12345678', 'qwerty123', 'admin123', 'test1234',
    'password123', 'admin', 'user1234', '87654321'
  ];

  if (commonWeakPasswords.includes(password.toLowerCase())) {
    return 'Please choose a stronger password';
  }

  // Check for repeating characters
  if (/(.)\1{3,}/.test(password)) {
    return 'Password cannot contain 4 or more repeating characters';
  }

  return null;
};

/**
 * Validates connection/referral source
 * @param {string} connection - Connection value
 * @returns {string|null} Error message or null if valid
 */
export const validateConnection = (connection) => {
  // This field is typically optional but let's validate if provided
  if (!connection || connection.trim().length === 0) {
    return null; // Optional field
  }

  const trimmedConnection = connection.trim();

  if (trimmedConnection.length > VALIDATION_LIMITS.CONNECTION_MAX_LENGTH) {
    logWarning('Extremely long connection field attempted', { length: trimmedConnection.length });
    return `Connection source cannot exceed ${VALIDATION_LIMITS.CONNECTION_MAX_LENGTH} characters`;
  }

  return null;
};

/**
 * Validates an entire form object
 * @param {Object} formData - Form data object
 * @param {Array} requiredFields - Array of required field names
 * @param {Object} customValidators - Custom validation functions for specific fields
 * @returns {Object} Object containing field errors
 */
export const validateForm = (formData, requiredFields = [], customValidators = {}) => {
  const errors = {};

  try {
    // Validate required fields first
    requiredFields.forEach(fieldName => {
      if (!formData[fieldName] || formData[fieldName].toString().trim().length === 0) {
        errors[fieldName] = `${fieldName.replace('_', ' ')} is required`;
      }
    });

    // Standard field validations
    if (formData.first_name !== undefined) {
      const firstNameError = validateFirstName(formData.first_name);
      if (firstNameError) errors.first_name = firstNameError;
    }

    if (formData.last_name !== undefined) {
      const lastNameError = validateLastName(formData.last_name);
      if (lastNameError) errors.last_name = lastNameError;
    }

    if (formData.email !== undefined) {
      const emailError = validateEmail(formData.email);
      if (emailError) errors.email = emailError;
    }

    if (formData.password !== undefined) {
      const passwordError = validatePassword(formData.password, process.env.NODE_ENV === 'production');
      if (passwordError) errors.password = passwordError;
    }

    if (formData.connection !== undefined) {
      const connectionError = validateConnection(formData.connection);
      if (connectionError) errors.connection = connectionError;
    }

    // Apply custom validators
    Object.keys(customValidators).forEach(fieldName => {
      if (formData[fieldName] !== undefined && !errors[fieldName]) {
        const customError = customValidators[fieldName](formData[fieldName], formData);
        if (customError) errors[fieldName] = customError;
      }
    });

    // Log validation attempts for monitoring
    if (Object.keys(errors).length > 0) {
      logWarning('Form validation failed', { 
        errorFields: Object.keys(errors),
        formFields: Object.keys(formData)
      });
    }

  } catch (error) {
    logError('Form validation error', { error: error.message, formData });
    errors._general = 'Validation error occurred. Please try again.';
  }

  return errors;
};

/**
 * Validates form fields in real-time (for onChange events)
 * @param {string} fieldName - Name of the field being validated
 * @param {string} value - Current field value
 * @param {Object} formData - Complete form data for context
 * @returns {string|null} Error message or null if valid
 */
export const validateFieldRealTime = (fieldName, value, formData = {}) => {
  try {
    switch (fieldName) {
      case 'first_name':
        return validateFirstName(value);
      case 'last_name':
        return validateLastName(value);
      case 'email':
        return validateEmail(value);
      case 'password':
        return validatePassword(value, process.env.NODE_ENV === 'production');
      case 'connection':
        return validateConnection(value);
      default:
        return validateRequired(value, fieldName.replace('_', ' '));
    }
  } catch (error) {
    logError('Real-time validation error', { fieldName, error: error.message });
    return 'Validation error occurred';
  }
};

export default {
  validateRequired,
  validateFirstName,
  validateLastName,
  validateEmail,
  validatePassword,
  validateConnection,
  validateForm,
  validateFieldRealTime,
  VALIDATION_LIMITS,
}; 